import Avatar from "@/components/base/Avatar";
import type { StepRankRow } from "@/lib/types";
import { formatNumber, medalFor } from "../utils";

interface Props {
  rows: StepRankRow[];
  myNo?: number;
}

// 걸음 랭킹 목록 — 1~3위는 카드로 강조, 4위부터는 한 줄 리스트.
// 걸음 수 아래 가로 막대로 1위 대비 비율을 보여준다.
export default function StepRankList({ rows, myNo }: Props) {
  if (rows.length === 0) return null;

  const top = rows.filter((r) => r.rank <= 3).slice(0, 3);
  const rest = rows.filter((r) => r.rank > 3);
  const maxSteps = Math.max(1, ...rows.map((r) => Number(r.steps) || 0));

  const isMine = (r: StepRankRow) =>
    r.is_mine === 1 || (myNo !== undefined && Number(r.mem_no) === Number(myNo));

  return (
    <div className="space-y-5">
      {/* 1~3위 */}
      {top.length > 0 && (
        <div className="grid grid-cols-3 items-end gap-3">
          {top.map((r) => {
            const medal = medalFor(r.rank);
            const mine = isMine(r);
            return (
              <div
                key={r.mem_no}
                className={`rounded-2xl border bg-background-50 p-3 text-center md:p-5 ${
                  r.rank === 1 ? "border-accent-300 md:pb-8" : "border-background-200"
                } ${mine ? "ring-2 ring-primary-300" : ""}`}
              >
                <div className="text-2xl" aria-hidden="true">
                  {medal}
                </div>
                <Avatar
                  name={r.name}
                  photo={r.photo}
                  size={r.rank === 1 ? 60 : 48}
                  className="mx-auto my-2"
                />
                <p className="truncate text-sm font-semibold text-foreground-900">
                  {r.aho ? `${r.aho} ` : ""}
                  {r.name}
                </p>
                <p className="mt-1 font-heading text-base font-bold tabular-nums text-foreground-950 md:text-lg">
                  {formatNumber(r.steps)}
                  <span className="ml-0.5 text-xs font-normal text-foreground-500">보</span>
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* 4위부터 */}
      {rest.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-background-200 bg-background-50">
          <ul className="divide-y divide-background-200">
            {rest.map((r) => {
              const mine = isMine(r);
              const pct = Math.round(((Number(r.steps) || 0) / maxSteps) * 100);
              return (
                <li
                  key={r.mem_no}
                  className={`flex items-center gap-3 px-4 py-3 ${mine ? "bg-primary-50" : ""}`}
                >
                  <span className="w-7 shrink-0 text-center font-heading text-sm font-bold tabular-nums text-foreground-500">
                    {r.rank}
                  </span>
                  <Avatar name={r.name} photo={r.photo} size={36} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-semibold text-foreground-900">
                        {r.aho ? `${r.aho} ` : ""}
                        {r.name}
                      </span>
                      {mine && (
                        <span className="shrink-0 rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-semibold text-primary-700">
                          나
                        </span>
                      )}
                    </div>
                    <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
                      <div
                        className={`h-full rounded-full ${mine ? "bg-primary-500" : "bg-secondary-500"}`}
                        style={{ width: `${pct}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="font-heading text-sm font-bold tabular-nums text-foreground-950">
                      {formatNumber(r.steps)}
                      <span className="ml-0.5 text-[11px] font-normal text-foreground-500">보</span>
                    </p>
                    {r.avg ? (
                      <p className="text-[11px] text-foreground-400">
                        하루 {formatNumber(r.avg)}
                      </p>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}